import { useCallback, useMemo, useState } from 'react'
import { FREE_EMAIL_DOMAINS } from '../constants'
import { validatePassword } from '../password-validation'

const useFormValidation = (formHTML, formFields, formValues) => {
  const [fieldErrors, setFieldErrors] = useState({})

  const validationRules = useMemo(() => {
    const rules = {}

    if (formFields?.length) {
      formFields.forEach(({ type: fieldType, props }) => {
        const { id, label, required, minLength, maxLength, type, freevalidation, passwordPolicy } = props
        if (!id) return
        if (!rules[id]) rules[id] = {}

        const labelLower = (label || '').toLowerCase()

        if (['zip code', 'postal code', 'zipcode'].some((s) => labelLower.includes(s))) {
          rules[id].regex = /^\d{5}(-\d{4})?$/
          rules[id].errorMessage = 'Invalid zip code format.'
          rules[id].isZipCode = true
        }

        if (type === 'email' || ['e-mail', 'email'].some((s) => labelLower.includes(s))) {
          rules[id].regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          rules[id].errorMessage = 'Invalid email format.'
          rules[id].isEmail = true
          const off = freevalidation === false || freevalidation === 'false'
          rules[id].freevalidation = !off
        }

        if (['phone', 'telephone'].some((s) => labelLower.includes(s))) {
          rules[id].regex = /^\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{4}$/
          rules[id].errorMessage = 'Invalid phone number format.'
        }

        if (required) {
          rules[id].required = true
          rules[id].errorMessage ||= 'This field is required.'
        }

        if (minLength) rules[id].minLength = parseInt(minLength, 10)
        if (maxLength) rules[id].maxLength = parseInt(maxLength, 10)
        if (passwordPolicy) {
          rules[id].passwordPolicy = true
          rules[id].errorMessage ||= 'Password requirements not met.'
        }
      })
    }

    if (formHTML && typeof window !== 'undefined') {
      try {
        const doc = new DOMParser().parseFromString(formHTML, 'text/html')
        doc.querySelectorAll('input, select, textarea').forEach((field) => {
          const fieldId = field.id
          if (!fieldId) return
          if (!rules[fieldId]) rules[fieldId] = {}

          const title = field.getAttribute('title') || ''
          const labelLower = title.toLowerCase()
          const fieldType = field.getAttribute('type')

          if (['zip code', 'postal code', 'zipcode'].some((s) => labelLower.includes(s))) {
            rules[fieldId].regex = /^\d{5}(-\d{4})?$/
            rules[fieldId].errorMessage = 'Invalid zip code format.'
            rules[fieldId].isZipCode = true
          }

          if (fieldType === 'email' || ['e-mail', 'email'].some((s) => labelLower.includes(s))) {
            rules[fieldId].regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            rules[fieldId].errorMessage = 'Invalid email format.'
            rules[fieldId].isEmail = true
            const attr = field.getAttribute('freevalidation')
            rules[fieldId].freevalidation = !(attr === 'false')
          }

          if (['phone', 'telephone'].some((s) => labelLower.includes(s))) {
            rules[fieldId].regex = /^\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{4}$/
            rules[fieldId].errorMessage = 'Invalid phone number format.'
          }

          let isReq =
            field.hasAttribute('required') || field.getAttribute('aria-required') === 'true'
          if (!isReq) {
            const parentId = field.getAttribute('data-tfa-parent-id')
            const parent = parentId && doc.getElementById(parentId)
            if (parent?.classList.contains('required')) isReq = true
          }
          if (isReq) {
            rules[fieldId].required = true
            rules[fieldId].errorMessage ||= 'This field is required.'
          }

          const minAttr = field.getAttribute('minlength')
          const maxAttr = field.getAttribute('maxlength')
          if (minAttr) rules[fieldId].minLength = parseInt(minAttr, 10)
          if (maxAttr) rules[fieldId].maxLength = parseInt(maxAttr, 10)
        })
      } catch (e) {
        console.error('Error parsing formHTML:', e)
      }
    }
    return rules
  }, [formFields, formHTML])

  const runChecks = useCallback(
    (id, value) => {
      const ruleset = validationRules[id]
      if (!ruleset) return ''
      const v = value?.toString().trim() || ''
      if (ruleset.required && v === '') return 'This field is required.'
      if (ruleset.minLength && v.length < ruleset.minLength) return `Minimum length is ${ruleset.minLength}.`
      if (ruleset.maxLength && v.length > ruleset.maxLength) return `Maximum length is ${ruleset.maxLength}.`
      if (ruleset.regex && v && !ruleset.regex.test(v)) return ruleset.errorMessage || 'Invalid format.'
      if (ruleset.passwordPolicy && v) {
        const { isValid, errorMessage } = validatePassword(v)
        if (!isValid) return errorMessage || 'Password requirements not met.'
      }
      if (ruleset.isEmail && v && ruleset.freevalidation && FREE_EMAIL_DOMAINS.includes(v.split('@')[1]?.toLowerCase()))
        return `Emails from '${v.split('@')[1]}' are not allowed.`
      return ''
    },
    [validationRules]
  )

  const validateField = useCallback(
    (id, value) => {
      const error = runChecks(id, value)
      setFieldErrors((e) => ({ ...e, [id]: error }))
      return { hasError: !!error, error }
    },
    [runChecks]
  )

  const validateAllFields = useCallback(() => {
    const newErrors = {}
    Object.keys(validationRules).forEach((id) => {
      const err = runChecks(id, formValues[id])
      if (err) newErrors[id] = err
    })
    setFieldErrors(newErrors)
    return { hasError: Object.keys(newErrors).length > 0, newFieldErrors: newErrors }
  }, [validationRules, formValues, runChecks])

  const clearErrors = useCallback(() => setFieldErrors({}), [])

  return {
    fieldErrors,
    validateField,
    validateAllFields,
    setFieldErrors,
    clearErrors,
    hasErrors: Object.keys(fieldErrors).length > 0,
    validationRules,
  }
}

export default useFormValidation
